import { NgForOf, NgIf } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { TranslatePipe } from '@ngx-translate/core';
import dayjs, { Dayjs } from 'dayjs';
import { sortBy } from 'lodash';
import { BehaviorSubject, combineLatest, filter, Subscription } from 'rxjs';
import { DateFormatPipe } from '../../../../core/pipes/date-format.pipe';
import { IDateRangePickerDate } from '../../../../shared/components/date-range-picker/date-range-picker.interface';
import { UserEffects } from '../../../../state/user/user.effects';
import { IItemsFilterCardFields } from '../../items-filter-card/items-filter-card.interface';
import { IBoardTreeStructure, IItemJoins } from '../../items.interface';
import { ItemsService } from '../../items.service';
import { IKamishibaiItem, ITableRow, TKamishibaiTableDateGroup } from './items-kamishibai.interface';
import { IKamishibaiViewStore } from './items-kamishibai.store';
import { KamishibaiCardComponent } from './kamishibai-card/kamishibai-card.component';

@Component({
  imports: [MatTableModule, NgForOf, DateFormatPipe, KamishibaiCardComponent, NgIf, TranslatePipe],
  providers: [IKamishibaiViewStore],
  selector: 'app-items-kamishibai',
  standalone: true,
  styleUrls: ['items-kamishibai.component.scss'],
  templateUrl: 'items-kamishibai.component.html',
})
export class ItemsKamishibaiComponent implements OnInit, OnDestroy {
  protected readonly subscriptions: Subscription[] = [];

  @Input({ required: true }) public filterCardData$ = new BehaviorSubject<Partial<IItemsFilterCardFields> | null>(null);
  @Input() public boardTreeStructure: IBoardTreeStructure[] = [];

  private readonly userTimezone: string;
  private dateRange: IDateRangePickerDate | null | undefined;

  public initialData: IKamishibaiItem[] = [];
  public items: IKamishibaiItem[] = [];

  public uniqueDates: string[] = [];
  public displayedColumns: string[] = [];
  public dataSource: ITableRow[] = [];
  public isSimpleCardMode = false;
  public monthText: string | undefined;
  public dayFormat = 'YYYY-MM-DD';
  public today: string = this.getToday();

  constructor(
    public readonly store: IKamishibaiViewStore,
    public readonly itemsService: ItemsService,
  ) {
    this.userTimezone = UserEffects.getUser().configuration.timezone;
    this.prepareDataSource(this.items);
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      this.store.data$.subscribe((data: IItemJoins<string>[]) => {
        this.initialData = this.prepareBoardInitData(this.dateRange);

        this.items = [];

        for (const item of data) {
          const formattedData: IKamishibaiItem | null = this.store.formatData(item);

          if (formattedData) {
            this.items.push(formattedData);
          }
        }

        this.prepareDataSource(this.items);
      }),
      combineLatest({
        filterCard: this.filterCardData$.pipe(filter(Boolean)),
      }).subscribe(({ filterCard }) => {
        if (filterCard.itemView !== 'kamishibai') {
          return;
        }

        this.dateRange = filterCard.dateRange;

        if (!this.items?.length) {
          this.prepareWithInitData();
        }

        this.loadItems(this.filterCardData$.value as IItemsFilterCardFields);
      }),

      this.itemsService.refreshItems$.subscribe(() => {
        this.loadItems(this.filterCardData$.value as IItemsFilterCardFields);
      }),
    );
  }

  public refreshDashboard(): void {
    this.itemsService.triggerRefresh();
  }

  private prepareWithInitData(): void {
    if (this.items?.length) {
      return;
    }

    this.initialData = this.prepareBoardInitData(this.dateRange);
    this.prepareDataSource([]);
  }

  private prepareDataSource(formattedItems: IKamishibaiItem[]): void {
    const items: IKamishibaiItem[] = sortBy(
      this.mergeInitialDataWithItems(this.initialData, formattedItems),
      [
        (item: IKamishibaiItem): number => {
          switch (item.group) {
            case 'daily':
              return 0;
            case 'weekly':
              return 1;
            case 'monthly':
              return 2;
            default:
              return 3;
          }
        },
      ],
      ['asc'],
    );

    this.uniqueDates = [...new Set(items.map((item) => item.date))].sort();
    this.displayedColumns = ['group', ...this.uniqueDates];

    const uniqueGroups: string[] = [...new Set(items.map((item: IKamishibaiItem): string => item.group))];

    this.dataSource = uniqueGroups.flatMap((groupName: string): ITableRow[] => {
      const groupItems = items.filter((item: IKamishibaiItem): boolean => item.group === groupName);

      return this.organizeGroupData(groupItems, groupName);
    });
  }

  private organizeGroupData(items: IKamishibaiItem[], groupName: string): ITableRow[] {
    const dateGroups: TKamishibaiTableDateGroup = items.reduce(
      (acc: TKamishibaiTableDateGroup, item: IKamishibaiItem): TKamishibaiTableDateGroup => {
        if (!acc[item.date]) {
          acc[item.date] = [];
        }

        acc[item.date].push(item);

        return acc;
      },
      {} as TKamishibaiTableDateGroup,
    );

    const maxRows: number = Math.max(...Object.values(dateGroups).map((g: IKamishibaiItem[]): number => g.length));
    const tableData: ITableRow[] = [];

    for (let i = 0; i < maxRows; i++) {
      const row: ITableRow = { group: i === 0 ? groupName : '', rowSpan: i === 0 ? maxRows : 0 };

      this.uniqueDates.forEach((date: string): void => {
        const itemsForDate: IKamishibaiItem[] = dateGroups[date] || [];
        row[date] = itemsForDate[i] ? itemsForDate[i].data : null;
      });

      tableData.push(row);
    }

    return tableData;
  }

  private getCroppedStartEndDate(startDate: Dayjs, endDate: Dayjs): [Dayjs, Dayjs] {
    this.monthText = undefined;
    let croppedEndDate: Dayjs = endDate;
    const oneMonthLater = startDate.add(1, 'month').startOf('day');

    if (endDate.isAfter(oneMonthLater)) {
      croppedEndDate = startDate.endOf('month').startOf('day');
      this.monthText = croppedEndDate.format('MMMM');
    }

    return [startDate, croppedEndDate];
  }

  private mergeInitialDataWithItems(initialData: IKamishibaiItem[], items: IKamishibaiItem[]): IKamishibaiItem[] {
    const itemsGrouped = new Map<string, IKamishibaiItem[]>();

    items.forEach((item: IKamishibaiItem): void => {
      const key = `${item.date}_${item.group}`;

      if (!itemsGrouped.has(key)) {
        itemsGrouped.set(key, []);
      }

      itemsGrouped.get(key)?.push(item);
    });

    const result: IKamishibaiItem[] = [];

    initialData.forEach((initialItem: IKamishibaiItem): void => {
      const key = `${initialItem.date}_${initialItem.group}`;
      const matchingItems: IKamishibaiItem[] | undefined = itemsGrouped.get(key);

      if (matchingItems && matchingItems.length > 0) {
        matchingItems.forEach((item) => {
          result.push({
            ...item,
            date: initialItem.date,
            group: initialItem.group,
          });
        });
      } else {
        result.push(initialItem);
      }
    });

    return sortBy(result, 'data.dueDate');
  }

  private prepareBoardInitData(dateRange?: IDateRangePickerDate | null): IKamishibaiItem[] {
    if (!dateRange || !dateRange?.startDate || !dateRange.endDate) {
      return [];
    }

    const days: IKamishibaiItem[] = [];

    const [dateRangeStartDate, dateRangeEndDate] = this.getCroppedStartEndDate(dateRange.startDate, dateRange.endDate);

    let currentDate = dateRangeStartDate.startOf('day');
    let endDate = dateRangeEndDate.startOf('day');

    const oneMonthLater = dateRange.startDate.add(1, 'month').startOf('day');

    if (endDate.isAfter(oneMonthLater)) {
      endDate = dateRange.startDate.endOf('month').startOf('day');
    }

    const diffDays = endDate.diff(currentDate, 'day') + 1;

    this.isSimpleCardMode = diffDays > 7;

    Array.from({ length: diffDays }).forEach((): void => {
      days.push({
        data: null,
        date: currentDate.format(this.dayFormat),
        group: 'daily',
      });
      days.push({
        data: null,
        date: currentDate.format(this.dayFormat),
        group: 'weekly',
      });
      days.push({
        data: null,
        date: currentDate.format(this.dayFormat),
        group: 'monthly',
      });

      currentDate = currentDate.add(1, 'day');
    });

    return days;
  }

  public getToday(): string {
    return dayjs().tz(this.userTimezone).format(this.dayFormat);
  }

  public loadItems(filterCard: IItemsFilterCardFields): void {
    if (!filterCard.dateRange || !filterCard.dateRange?.startDate || !filterCard.dateRange.endDate) {
      return;
    }

    const [dateRangeStartDate, dateRangeEndDate] = this.getCroppedStartEndDate(
      filterCard.dateRange.startDate,
      filterCard.dateRange.endDate,
    );

    filterCard.dateRange = { endDate: dateRangeEndDate, startDate: dateRangeStartDate };

    this.store.loadItems({
      additionalCustomSearch: this.itemsService.prepareAdditionalCustomSearch(filterCard, this.boardTreeStructure),
      fields: ['name', 'key', 'dueDate'],
      filters: [...this.itemsService.prepareFilters(filterCard)],
      join: [
        'board||name,key',
        'currentWorkflowStep||name,isDefault,statusCategory',
        'assignee||name',
        'boardItemConfiguration||name,key,itemType',
        'itemRecurrence||cron',
      ],
      limit: 1000,
      page: 1,
    });
  }

  public ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription?.unsubscribe();
    }
  }
}
